import { isAxiosError } from 'axios';

/**
 * Extract a human-readable error message from an unknown error.
 *
 * API error format: { message: "...", error_code: "...", request_id: "..." }
 * Matches Laravel's native `message` convention (Stripe/Plaid-inspired).
 */
export function getErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    // Industry-standard: { message: '...' } at root (Laravel native + our error format)
    if (data?.message) {
      return data.message;
    }
    return error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

/**
 * Extract the machine-readable error code from an API error response.
 * Returns null if no error code is present.
 */
export function getErrorCode(error: unknown): string | null {
  if (isAxiosError(error)) {
    return error.response?.data?.error_code ?? null;
  }
  return null;
}

/**
 * Extract the request ID from an API error response for debugging.
 * Can be found in response body or X-Request-Id header.
 */
export function getRequestId(error: unknown): string | null {
  if (isAxiosError(error)) {
    return (
      error.response?.data?.request_id ??
      error.response?.headers?.['x-request-id'] ??
      null
    );
  }
  return null;
}
